import React, { useRef, useState, useEffect } from 'react';
import { Chip, Tooltip } from '@mui/material';

/**
 * Custom component that wraps Material-UI Chips with tooltips only when text is truncated
 *
 * @param {string} label - The text to display in the chip
 * @param {string} tooltip - Optional custom tooltip text (defaults to label if not provided)
 * @param {object} style - The style object for the Chip
 * @param {string} className - Additional CSS class name
 * @param {object} rest - Additional props to pass to the Chip component
 * @returns {React.ReactNode} The rendered Chip component
 */
const SmartTooltipChip = ({ label, tooltip, style, className, ...rest }) => {
  const chipRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Use tooltip if provided, otherwise fall back to label
  const tooltipContent = tooltip || label;

  useEffect(() => {
    const checkOverflow = () => {
      if (chipRef.current) {
        const labelElement = chipRef.current.querySelector('.MuiChip-label');
        if (labelElement) {
          setIsOverflowing(labelElement.scrollWidth > labelElement.clientWidth);
        }
      }
    };

    // Initial check
    checkOverflow();

    // Also check after any potential layout changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (chipRef.current) {
      resizeObserver.observe(chipRef.current);
    }

    return () => {
      if (chipRef.current) {
        resizeObserver.unobserve(chipRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [label]);

  /**
   * Overriding the default behavior of the tooltip (when there is too much data in the chip, the chip label is the tooltip)
   * This will work if we don't expect the chip to be truncated. We can't really have 2 ToolTips on the same chip.
   */
  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterTouchDelay={0}
      enterDelay={100}
      disableHoverListener={!isOverflowing && !tooltip}
      disableTouchListener={!isOverflowing && !tooltip}
      disableFocusListener={!isOverflowing && !tooltip}
    >
      <Chip
        ref={chipRef}
        label={label}
        style={style}
        className={className}
        {...rest}
      />
    </Tooltip>
  );
};

export default SmartTooltipChip;
